import type { PayoutPartyResponse } from 'payments-api/model';
import {
  useGetInvoicesSuspense,
  useGetPartiesSuspense,
} from 'api/payments/generated/fragmentPaymentsAPI';
import {
  PaymentViewHeader,
  PaymentViewLayout,
} from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense, useMemo } from 'react';
import { makePath } from 'utils/routes';
import useLocation from 'wouter/use-location';

import { TertiaryButton } from '@fragment/ui/src/components/Button/TertiaryButton/TertiaryButton';

import { PartyView } from './PartyView';

// Helper to check if payout party matches a given externalId
const isPayoutPartyMatch = (
  payoutParty: PayoutPartyResponse,
  externalId: string
): boolean => {
  if ('platform' in payoutParty && payoutParty.platform) {
    return false;
  }
  return 'party_id' in payoutParty && payoutParty.party_id === externalId;
};

type Props = {
  workspaceId: string;
  externalId: string;
};

const PublicPartyViewPageContent = ({
  workspaceId,
  externalId,
}: Props): JSX.Element => {
  useDocumentTitle('Party Portal');

  const [, setLocation] = useLocation();

  const { data: partiesData } = useGetPartiesSuspense();
  const { data: invoicesData } = useGetInvoicesSuspense();

  // Find the specific party by externalId
  const party = useMemo(
    () => partiesData.data.find((p) => p.externalId === externalId),
    [partiesData, externalId]
  );

  // Find all invoices that include this party
  const partyInvoices = useMemo(
    () =>
      invoicesData.data.filter((invoice) => {
        if (!invoice.lineItems || invoice.lineItems.length === 0) return false;
        return invoice.lineItems.some(
          (lineItem) =>
            invoice.buyerParty === externalId ||
            isPayoutPartyMatch(lineItem.payout_party, externalId)
        );
      }),
    [invoicesData, externalId]
  );

  if (!party) {
    return (
      <PaymentViewLayout
        header={
          <PaymentViewHeader>
            <div className="flex justify-between w-full">
              <h1 className="text-xl font-main">{externalId} Portal</h1>
              <TertiaryButton
                onClick={() =>
                  setLocation(
                    makePath(
                      ['w', workspaceId, 'payments', 'parties', externalId],
                      {
                        absolute: true,
                      }
                    )
                  )
                }
              >
                Go back
              </TertiaryButton>
            </div>
          </PaymentViewHeader>
        }
      >
        <div className="flex items-center justify-center h-full">
          <div className="text-red-500 text-main">
            Party not found: {externalId}
          </div>
        </div>
      </PaymentViewLayout>
    );
  }

  return (
    <PaymentViewLayout
      header={
        <PaymentViewHeader>
          <div className="flex justify-between w-full h-full">
            <h1 className="text-xl font-main">{externalId} Portal</h1>
            <TertiaryButton
              onClick={() =>
                setLocation(
                  makePath(
                    ['w', workspaceId, 'payments', 'parties', externalId],
                    {
                      absolute: true,
                    }
                  )
                )
              }
            >
              Go back
            </TertiaryButton>
          </div>
        </PaymentViewHeader>
      }
    >
      <div className="max-w-5xl mx-auto">
        <PartyView
          party={party}
          partyInvoices={partyInvoices}
          externalId={externalId}
          workspaceId={workspaceId}
          enableInvoiceNavigation={false}
        />
      </div>
    </PaymentViewLayout>
  );
};

export const PublicPartyViewPage = ({
  workspaceId,
  externalId,
}: Props): JSX.Element => (
  <Suspense
    fallback={
      <PaymentViewLayout
        header={
          <PaymentViewHeader>
            <div className="flex justify-between w-full">
              <h1 className="text-xl font-main">{externalId} Portal</h1>
            </div>
          </PaymentViewHeader>
        }
      >
        <div className="flex items-center justify-center h-full">
          <div className="text-main-500 text-main">Loading...</div>
        </div>
      </PaymentViewLayout>
    }
  >
    <PublicPartyViewPageContent
      workspaceId={workspaceId}
      externalId={externalId}
    />
  </Suspense>
);
